import { useEffect } from "react";
import { Home, UtensilsCrossed, ShoppingBag, User, ClipboardList } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { cn } from "@/lib/utils";
import { Footer } from "@/components/Footer";
import axios from "axios";
import API_URL from "@/utils/api";

const TABLE_QR_KEY = "app_qr_table";

export const MobileLayout = ({ children, showFooter = false }) => {
  const location = useLocation();
  const { getItemCount } = useCart();
  const itemCount = getItemCount();

  // Check if customer is ordering from QR (table mode)
  const isTableMode = !!localStorage.getItem(TABLE_QR_KEY);

  // Branding injection
  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await axios.get(`${API_URL}/config`);
        const data = response.data;
        const color = data.primary_color || data.branding?.primary_color;
        if (color) {
          document.documentElement.style.setProperty('--primary', color);
          document.documentElement.style.setProperty('--accent', color);
          
          // Derive RGB for opacity/glows
          if (color.startsWith('#')) {
            const r = parseInt(color.slice(1, 3), 16);
            const g = parseInt(color.slice(3, 5), 16);
            const b = parseInt(color.slice(5, 7), 16);
            document.documentElement.style.setProperty('--primary-rgb', `${r}, ${g}, ${b}`);
          }
        }

        // Update Title and Favicon
        const name = data.restaurant_name || data.name;
        if (name) {
          document.title = `${name} — Pedidos Online`;
        }
        
        const logo = data.logo || data.branding?.logo_url;
        if (logo) {
          const favicon = document.querySelector('link[rel="icon"]');
          if (favicon) favicon.href = logo;
          const appleIcon = document.querySelector('link[rel="apple-touch-icon"]');
          if (appleIcon) appleIcon.href = logo;
        }
      } catch (e) {
        console.error("Error loading branding in MobileLayout:", e);
      }
    };
    fetchBranding();
  }, []);

  // Navigation items - simplified for table/QR mode
  const navItems = isTableMode
    ? [
      { path: "/menu", icon: UtensilsCrossed, label: "Menú" },
      { path: "/cart", icon: ShoppingBag, label: "Carrito", badge: itemCount },
    ]
    : [
      { path: "/", icon: Home, label: "Inicio" },
      { path: "/menu", icon: UtensilsCrossed, label: "Menú" },
      { path: "/cart", icon: ShoppingBag, label: "Carrito", badge: itemCount },
      { path: "/orders", icon: ClipboardList, label: "Pedidos" },
      { path: "/profile", icon: User, label: "Perfil" },
    ];

  return (
    <div style={{ background: 'var(--bg-deep)', minHeight: '100vh', display: 'flex', flexDirection: 'column' }} className="page-fade-in">
      <main className="pb-24 flex-1">{children}</main>

      {showFooter && <Footer />}

      {/* Bottom Navigation */}
      <nav
        style={{
          background: 'rgba(2, 6, 23, 0.8)',
          backdropFilter: 'blur(20px)',
          borderTop: '1px solid var(--border-soft)',
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 50
        }}
        className="safe-area-pb"
      >
        <div className={cn(
          "flex items-center py-2 px-2",
          isTableMode ? "justify-center gap-16" : "justify-around"
        )}>
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path ||
              (item.path === "/menu" && location.pathname.startsWith("/menu/"));

            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={cn(
                  "flex flex-col items-center gap-1 px-3 py-2 rounded-xl transition-all relative",
                  isActive
                    ? "text-primary"
                    : "text-slate-500 hover:text-slate-300"
                )}
                style={{
                  color: isActive ? 'var(--primary)' : 'var(--text-soft)',
                  transform: isActive ? 'translateY(-2px)' : 'none'
                }}
              >
                <div className="relative">
                  <Icon className="w-5 h-5" style={{ color: isActive ? 'var(--primary)' : 'inherit' }} />
                  {item.badge > 0 && (
                    <span
                      style={{
                        background: 'var(--accent)',
                        color: 'black',
                        boxShadow: '0 0 10px rgba(245, 158, 11, 0.4)'
                      }}
                      className="absolute -top-2 -right-2 w-5 h-5 text-[10px] font-black rounded-full flex items-center justify-center cart-badge"
                    >
                      {item.badge}
                    </span>
                  )}
                  {isActive && (
                    <div style={{ position: 'absolute', bottom: '-8px', left: '50%', transform: 'translateX(-50%)', width: '4px', height: '4px', background: 'var(--primary)', borderRadius: '50%', boxShadow: '0 0 8px var(--primary)' }} />
                  )}
                </div>
                <span style={{ fontSize: '0.65rem', fontWeight: isActive ? '700' : '500', marginTop: '4px' }}>{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default MobileLayout;
